import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import AmbientBackground from "../components/AmbientBackground";
import { trackEvent } from "../lib/analytics";
import {
  AnimatedSection,
  ContentPageInner,
  ContentPageShell,
  GlassPanel,
  PrimaryButton,
  SecondaryButton,
  SectionKicker,
} from "../styles/contentPage";

function trackNexClick(label, destination, source) {
  trackEvent("cta_click", {
    path: "/nex",
    label,
    destination,
    source,
    page_group: "nex",
  });
}

function NexHeroVisual({ visual }) {
  const normalizeNode = (item) => (typeof item === "string" ? { title: item, text: "" } : item);
  const wrapNodeCopy = (text = "") => {
    if (!text) return [];

    return text
      .split(" · ")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => part.charAt(0).toLocaleUpperCase() + part.slice(1));
  };
  const renderNodeIcon = (kind) => {
    if (kind === "channels") {
      return (
        <>
          <path d="M7 11H33V28H7Z" />
          <path d="M7 11L20 21L33 11" />
          <path className="icon-soft" d="M10 35H18M23 35H30" />
        </>
      );
    }

    if (kind === "context") {
      return (
        <>
          <path d="M8 13C8 8 32 8 32 13V29C32 34 8 34 8 29Z" />
          <path d="M8 13C8 18 32 18 32 13" />
          <path d="M8 22C8 27 32 27 32 22" />
          <path className="icon-soft" d="M30 30L36 36" />
        </>
      );
    }

    if (kind === "workflow") {
      return (
        <>
          <rect x="8" y="9" width="10" height="10" rx="3" />
          <rect x="24" y="9" width="10" height="10" rx="3" />
          <rect x="16" y="27" width="10" height="10" rx="3" />
          <path d="M18 14H24M29 19L24 27M13 19L18 27" />
        </>
      );
    }

    if (kind === "approvals") {
      return (
        <>
          <path d="M20 6L34 12V24C34 34 26 39 20 42C14 39 6 34 6 24V12Z" />
          <path d="M13 23L18 28L28 17" />
        </>
      );
    }

    if (kind === "memory") {
      return (
        <>
          <rect x="9" y="9" width="24" height="29" rx="6" />
          <path d="M15 18H28M15 26H25M15 34H30" />
          <path className="icon-soft" d="M31 9C38 15 38 30 31 38" />
        </>
      );
    }

    return (
      <>
        <path d="M8 12H33V32H8Z" />
        <path d="M16 20H28M16 26H24" />
        <path className="icon-soft" d="M31 22H39M35 18L39 22L35 26" />
      </>
    );
  };
  const inputs = Array.isArray(visual?.inputs) ? visual.inputs.map(normalizeNode) : [];
  const outputs = Array.isArray(visual?.outputs) ? visual.outputs : [];
  const core = { x: 410, y: 280, r: 126 };
  const nodeCenters = [142, 280, 418];
  const nodeSize = { width: 196, height: 126 };
  const getCoreAnchorX = (side, y) => {
    const deltaY = y - core.y;
    const deltaX = Math.sqrt(Math.max(core.r * core.r - deltaY * deltaY, 0));
    return core.x + side * deltaX;
  };
  const inputNodes = inputs.map((label, index) => ({
    label,
    x: 56,
    y: nodeCenters[index] - nodeSize.height / 2,
    centerY: nodeCenters[index],
    width: nodeSize.width,
    height: nodeSize.height,
    kind: ["channels", "context", "workflow"][index],
    anchorX: getCoreAnchorX(-1, nodeCenters[index]),
  }));
  const outputNodes = outputs.map((label, index) => ({
    label,
    x: 568,
    y: nodeCenters[index] - nodeSize.height / 2,
    centerY: nodeCenters[index],
    width: nodeSize.width,
    height: nodeSize.height,
    kind: ["approvals", "memory", "output"][index],
    anchorX: getCoreAnchorX(1, nodeCenters[index]),
  }));

  return (
    <HeroVisualPanel aria-label={visual?.ariaLabel}>
      <svg viewBox="0 0 820 560" role="img" focusable="false">
        <defs>
          <filter id="nexHeroGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="7" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="nexHeroCard" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.12)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
          </linearGradient>
          <linearGradient id="nexHeroFlow" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="rgba(34,211,238,0)" />
            <stop offset="44%" stopColor="rgba(188,244,255,0.98)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0.74)" />
          </linearGradient>
          <radialGradient id="nexCoreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(34,211,238,0.42)" />
            <stop offset="58%" stopColor="rgba(34,211,238,0.16)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0)" />
          </radialGradient>
        </defs>

        <rect className="hero-shell" x="24" y="24" width="772" height="512" rx="42" />
        <g className="hero-grid-lines">
          {[118, 208, 298, 388, 478, 568, 658, 748].map((x) => (
            <path key={`v-${x}`} d={`M${x} 56V504`} />
          ))}
          {[120, 200, 280, 360, 440].map((y) => (
            <path key={`h-${y}`} d={`M48 ${y}H772`} />
          ))}
        </g>

        {inputNodes.map((node, index) => {
          const startX = node.x + node.width;
          const linePath = `M${startX} ${node.centerY}H${node.anchorX}`;

          return (
            <React.Fragment key={node.label.title}>
              <path
                className="hero-track"
                pathLength="100"
                d={linePath}
              />
              <path
                className={`hero-flow hero-flow-in hero-flow-${index + 1}`}
                pathLength="100"
                d={linePath}
              />
              <g className={`hero-node hero-node-${index + 1} input-node`} transform={`translate(${node.x} ${node.y})`}>
                <rect className="node-glow" x="-8" y="-8" width={node.width + 16} height={node.height + 16} rx="28" />
                <rect className="node-card" width={node.width} height={node.height} rx="21" />
                <rect className="node-edge" width={node.width} height={node.height} rx="21" />
                <path className="node-accent" d={`M28 16H${node.width - 28}`} />
                <g className="node-icon" transform="translate(24 34)">
                  {renderNodeIcon(node.kind)}
                </g>
                <text className="node-title" x="76" y="43">{node.label.title}</text>
                {node.label.text ? (
                  <text className="node-sub" x="76" y="71">
                    {wrapNodeCopy(node.label.text).map((line, lineIndex) => (
                      <tspan key={line} x="76" dy={lineIndex === 0 ? 0 : 29}>
                        {line}
                      </tspan>
                    ))}
                  </text>
                ) : null}
                <circle className="node-dot" cx={node.width} cy={node.height / 2} r="4.2" />
              </g>
            </React.Fragment>
          );
        })}

        <g className="hero-core" transform={`translate(${core.x} ${core.y})`}>
          <circle className="core-halo" r="136" />
          <circle className="core-pulse-ring" r="130" />
          <circle className="core-orbit core-orbit-a" r="104" />
          <circle className="core-orbit core-orbit-b" r="78" />
          <circle className="core-ring" r={core.r} />
          <text className="core-kicker" y="-34">NEX</text>
          <text className="core-title" y="12">{visual?.coreTitle}</text>
          <text className="core-text" y="47">{visual?.coreText}</text>
        </g>

        {outputNodes.map((node, index) => {
          const linePath = `M${node.anchorX} ${node.centerY}H${node.x}`;

          return (
            <React.Fragment key={node.label.title}>
              <path
                className="hero-track"
                pathLength="100"
                d={linePath}
              />
              <path
                className={`hero-flow hero-flow-out hero-flow-${index + 4}`}
                pathLength="100"
                d={linePath}
              />
              <g className={`hero-node hero-node-${index + 4} output-node`} transform={`translate(${node.x} ${node.y})`}>
                <rect className="node-glow" x="-8" y="-8" width={node.width + 16} height={node.height + 16} rx="28" />
                <rect className="node-card" width={node.width} height={node.height} rx="21" />
                <rect className="node-edge" width={node.width} height={node.height} rx="21" />
                <path className="node-accent" d={`M28 16H${node.width - 28}`} />
                <g className="node-icon" transform="translate(24 34)">
                  {renderNodeIcon(node.kind)}
                </g>
                <text className="node-title" x="76" y="43">{node.label.title}</text>
                <text className="node-sub" x="76" y="71">
                  {wrapNodeCopy(node.label.text).map((line, lineIndex) => (
                    <tspan key={line} x="76" dy={lineIndex === 0 ? 0 : 29}>
                      {line}
                    </tspan>
                  ))}
                </text>
                <circle className="node-dot" cx="0" cy={node.height / 2} r="4.2" />
              </g>
            </React.Fragment>
          );
        })}
      </svg>
    </HeroVisualPanel>
  );
}

export default function NexPage() {
  const { t } = useTranslation();
  const data = t("nexPage", { returnObjects: true });

  return (
    <Wrap className="page-container">
      <AmbientBackground />
      <Inner>
        <AnimatedSection $i={0}>
          <HeroGrid>
            <HeroCopy>
              <NexKicker>{data.hero?.kicker}</NexKicker>
              <HeroTitle>{data.hero?.title}</HeroTitle>
              <HeroLead>{data.hero?.lead}</HeroLead>
              <HeroActions>
                <PrimaryButton
                  as={Link}
                  to="/contact"
                  onClick={() =>
                    trackNexClick(data.hero?.primaryCta, "/contact", "nex-hero-primary")
                  }
                >
                  {data.hero?.primaryCta}
                </PrimaryButton>
                <SecondaryButton
                  as={Link}
                  to="/work"
                  onClick={() => trackNexClick(data.hero?.secondaryCta, "/work", "nex-hero-secondary")}
                >
                  {data.hero?.secondaryCta}
                </SecondaryButton>
              </HeroActions>
            </HeroCopy>
            <NexHeroVisual visual={data.hero?.visual} />
          </HeroGrid>
        </AnimatedSection>

        <AnimatedSection $i={1}>
          <FinalPanel>
            <FinalCopy>
              <SectionKicker>{data.cta?.kicker}</SectionKicker>
              <FinalTitle>{data.cta?.title}</FinalTitle>
              <FinalText>{data.cta?.body}</FinalText>
            </FinalCopy>
            <FinalActions>
              <PrimaryButton
                as={Link}
                to="/contact"
                onClick={() => trackNexClick(data.cta?.primary, "/contact", "nex-final-primary")}
              >
                {data.cta?.primary}
              </PrimaryButton>
              {data.cta?.secondary ? (
                <SecondaryButton
                  as={Link}
                  to="/work"
                  onClick={() => trackNexClick(data.cta.secondary, "/work", "nex-final-secondary")}
                >
                  {data.cta.secondary}
                </SecondaryButton>
              ) : null}
            </FinalActions>
          </FinalPanel>
        </AnimatedSection>
      </Inner>
    </Wrap>
  );
}

const heroLaneFlow = keyframes`
  0% { stroke-dashoffset: 100; opacity: 0; }
  8% { opacity: 0.98; }
  78% { opacity: 0.98; }
  100% { stroke-dashoffset: 0; opacity: 0; }
`;

const coreGlow = keyframes`
  0%, 100% { opacity: 0.62; }
  50% { opacity: 0.92; }
`;

const coreOrbit = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const nodeGlow = keyframes`
  0%, 100% { opacity: 0; }
  36%, 58% { opacity: 0.42; }
`;

const Wrap = styled(ContentPageShell)`
  --nex-accent: #22d3ee;
  --nex-accent-soft: rgba(34, 211, 238, 0.18);
  --nex-accent-border: rgba(34, 211, 238, 0.28);
`;

const Inner = styled(ContentPageInner)``;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(460px, 1.08fr);
  gap: clamp(32px, 5vw, 68px);
  align-items: center;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  display: grid;
  gap: clamp(18px, 2.4vw, 26px);
`;

const NexKicker = styled.span`
  width: max-content;
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid var(--nex-accent-border);
  background: rgba(34, 211, 238, 0.08);
  color: rgba(188, 244, 255, 0.95);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.22em;
`;

const HeroTitle = styled.h1`
  margin: 0;
  max-width: 760px;
  font-size: clamp(48px, 7vw, 86px);
  line-height: 0.94;
  letter-spacing: -0.055em;
  font-weight: 850;
`;

const HeroLead = styled.p`
  margin: 0;
  max-width: 640px;
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(18px, 2vw, 22px);
  line-height: 1.72;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const HeroVisualPanel = styled(GlassPanel)`
  position: relative;
  overflow: hidden;
  border-radius: 34px;
  border-color: var(--nex-accent-border);
  background:
    radial-gradient(circle at 56% 52%, rgba(34, 211, 238, 0.16), transparent 36%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.075), rgba(8, 20, 24, 0.22)),
    rgba(3, 7, 9, 0.72);

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  .hero-shell {
    fill: rgba(0, 0, 0, 0.34);
    stroke: rgba(255, 255, 255, 0.12);
  }

  .hero-grid-lines path {
    stroke: rgba(255, 255, 255, 0.045);
    stroke-width: 1;
  }

  .node-glow {
    fill: rgba(34, 211, 238, 0.065);
    opacity: 0;
    filter: url(#nexHeroGlow);
    animation: ${nodeGlow} 6.8s ease-in-out infinite;
  }

  .node-card {
    fill: url(#nexHeroCard);
    stroke: rgba(34, 211, 238, 0.2);
    stroke-width: 1.45;
  }

  .node-edge {
    fill: none;
    stroke: rgba(188, 244, 255, 0.62);
    stroke-width: 1.45;
    opacity: 0;
    filter: url(#nexHeroGlow);
    animation: ${nodeGlow} 6.8s ease-in-out infinite;
  }

  .hero-node text {
    fill: rgba(255, 255, 255, 0.92);
    font-size: 18px;
    font-weight: 800;
    font-family: var(--font-body);
    letter-spacing: 0;
  }

  .node-title {
    font-size: 14.4px;
    font-weight: 900;
  }

  .node-sub {
    fill: rgba(255, 255, 255, 0.66);
    font-size: 9.15px;
    font-weight: 700;
  }

  .node-accent {
    fill: none;
    stroke: rgba(188, 244, 255, 0.14);
    stroke-width: 2.8;
    stroke-linecap: round;
  }

  .node-icon {
    fill: none;
    stroke: rgba(218, 250, 255, 0.9);
    stroke-width: 2.45;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0 0 8px rgba(34, 211, 238, 0.18));
  }

  .node-icon rect {
    fill: rgba(34, 211, 238, 0.08);
    stroke: rgba(218, 250, 255, 0.86);
  }

  .node-icon .icon-soft {
    stroke: rgba(34, 211, 238, 0.78);
  }

  .hero-track {
    fill: none;
    stroke: rgba(34, 211, 238, 0.22);
    stroke-width: 3.2;
    stroke-linecap: round;
  }

  .hero-flow {
    fill: none;
    stroke: rgba(188, 244, 255, 0.82);
    stroke-width: 4.6;
    stroke-linecap: round;
    stroke-dasharray: 48 52;
    stroke-dashoffset: 100;
    animation: ${heroLaneFlow} 6.8s linear infinite;
    filter: url(#nexHeroGlow);
  }

  .hero-flow-2,
  .hero-flow-5 {
    animation-delay: -1.15s;
  }

  .hero-flow-3,
  .hero-flow-6 {
    animation-delay: -2.35s;
  }

  .hero-flow-4 {
    animation-delay: -0.45s;
  }

  .hero-node-2 .node-glow,
  .hero-node-2 .node-edge,
  .hero-node-5 .node-glow,
  .hero-node-5 .node-edge {
    animation-delay: -1.15s;
  }

  .hero-node-3 .node-glow,
  .hero-node-3 .node-edge,
  .hero-node-6 .node-glow,
  .hero-node-6 .node-edge {
    animation-delay: -2.35s;
  }

  .hero-node-4 .node-glow,
  .hero-node-4 .node-edge {
    animation-delay: -0.45s;
  }

  .node-dot {
    fill: rgba(188, 244, 255, 0.95);
    filter: url(#nexHeroGlow);
  }

  .core-halo {
    fill: url(#nexCoreGlow);
    animation: ${coreGlow} 5.4s ease-in-out infinite;
    opacity: 0.72;
  }

  .core-pulse-ring,
  .core-orbit {
    fill: none;
    stroke-linecap: round;
    transform-box: fill-box;
    transform-origin: center;
  }

  .core-pulse-ring {
    stroke: rgba(34, 211, 238, 0.09);
    stroke-width: 14;
    animation: ${coreGlow} 4.8s ease-in-out infinite;
  }

  .core-orbit {
    stroke: rgba(188, 244, 255, 0.24);
    stroke-width: 1.5;
    stroke-dasharray: 14 22;
    animation: ${coreOrbit} 18s linear infinite;
  }

  .core-orbit-b {
    stroke: rgba(34, 211, 238, 0.18);
    animation-duration: 24s;
    animation-direction: reverse;
  }

  .core-ring {
    fill: rgba(0, 0, 0, 0.48);
    stroke: rgba(34, 211, 238, 0.78);
    stroke-width: 2;
    filter: drop-shadow(0 0 16px rgba(34, 211, 238, 0.24));
  }

  .core-kicker,
  .core-title,
  .core-text {
    text-anchor: middle;
    fill: #fff;
  }

  .core-kicker {
    fill: rgba(34, 211, 238, 0.95);
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.18em;
  }

  .core-title {
    font-size: 28px;
    font-weight: 900;
  }

  .core-text {
    fill: rgba(255, 255, 255, 0.68);
    font-size: 12.5px;
    font-weight: 750;
  }

  @media (max-width: 720px) {
    margin-inline: calc(var(--content-gutter) * -0.5);
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
    }
  }
`;

const FinalPanel = styled(GlassPanel)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: clamp(22px, 4vw, 42px);
  align-items: center;
  padding: clamp(28px, 4vw, 46px);
  border-radius: 30px;
  border-color: var(--nex-accent-border);
  background:
    radial-gradient(circle at 20% 0%, rgba(34, 211, 238, 0.12), transparent 34%),
    rgba(4, 7, 9, 0.72);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const FinalCopy = styled.div`
  display: grid;
  gap: 12px;
`;

const FinalTitle = styled.h2`
  margin: 0;
  font-size: clamp(34px, 5vw, 62px);
  line-height: 0.98;
  letter-spacing: -0.045em;
`;

const FinalText = styled.p`
  margin: 0;
  max-width: 760px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 18px;
  line-height: 1.65;
`;

const FinalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 860px) {
    justify-content: flex-start;
  }
`;
